import * as React from 'react';
import { useEffect, useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import store from 'js/lib/coursera.store';
import { useRetracked } from 'js/lib/retracked';
import { useRouter } from 'js/lib/useRouter';
import user from 'js/lib/user';

import { useTracker } from '@coursera/event-pulse/react';

import { HAS_PREVIOUSLY_LOGGED_IN } from 'bundles/authentication-common/constants';
import useIsInLegalConsolidationFlowAndHasTouQueryParam from 'bundles/authentication-common/hooks/useIsInLegalConsolidationFlowAndHasTouQueryParam';
import PartnerSupportedAccessModal from 'bundles/enroll-common/components/freemium/PartnerSupportedAccessModal';
import { useEnrollment } from 'bundles/enroll-common/components/xdp/withEnrollment';
import { SHOW_PARTNER_SUPPORTED_ACCESS } from 'bundles/enroll-common/utils/enrollActionParams';
import googleCertificateExperiments from 'bundles/epic/clients/GoogleCertificate';

import _t from 'i18n!nls/enroll';

type Props = {
  courseId?: string;
  courseSlug?: string;
};

const PartnerSupportedAccessLink = ({ courseId, courseSlug }: Props) => {
  const [showModal, setShowModal] = useState(false);
  const retrackedTrack = useRetracked();
  const router = useRouter();
  const { enrollmentAvailableChoices } = useEnrollment({ courseId, skip: !courseId });
  const track = useTracker();
  const isLegalConsolidationFlowAndHasTouQueryParam = useIsInLegalConsolidationFlowAndHasTouQueryParam();

  const isPartnerSupportedAccessEnabled =
    (!user.isAuthenticatedUser() ||
      enrollmentAvailableChoices?.hasFreeEnrollOptionIntoCourse ||
      enrollmentAvailableChoices?.canAuditCourse) &&
    courseId &&
    // eslint-disable-next-line camelcase
    googleCertificateExperiments.preview('isPartnerSupportedAccessEnabled', { course_id: courseId });

  const toggleModal = () => setShowModal(!showModal);
  const onClick = () => {
    track('click_button', {
      button: {
        name: 'partner_supported_access_learn_more',
      },
      product: {
        id: courseId!,
        type: 'course',
      },
    });

    if (!user.isAuthenticatedUser()) {
      const hasPreviouslyLoggedIn = store.get(HAS_PREVIOUSLY_LOGGED_IN);

      router.replace({
        pathname: router.location.pathname,
        query: {
          ...router.location.query,
          authMode: hasPreviouslyLoggedIn ? 'login' : 'signup',
          action: SHOW_PARTNER_SUPPORTED_ACCESS,
        },
      });

      if (hasPreviouslyLoggedIn) {
        retrackedTrack({
          trackingName: HAS_PREVIOUSLY_LOGGED_IN,
          action: 'show',
          trackingData: {},
        });
      }
    } else {
      toggleModal();
    }
  };

  useEffect(() => {
    const showModalFromQueryParam = router.location.query?.action === SHOW_PARTNER_SUPPORTED_ACCESS;

    if (showModalFromQueryParam && !isLegalConsolidationFlowAndHasTouQueryParam && isPartnerSupportedAccessEnabled) {
      onClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLegalConsolidationFlowAndHasTouQueryParam]);

  if (!courseId || !courseSlug || !isPartnerSupportedAccessEnabled) {
    return null;
  }

  return (
    <>
      <FormattedMessage
        message={_t('Partner-supported free access, {learnMore}')}
        learnMore={
          <button type="button" className="button-link" onClick={onClick}>
            {_t('learn more')}
          </button>
        }
      />
      {showModal && (
        <PartnerSupportedAccessModal
          courseId={courseId}
          courseSlug={courseSlug}
          hasFreeEnrollOption={enrollmentAvailableChoices?.hasFreeEnrollOptionIntoCourse}
          onClose={toggleModal}
        />
      )}
    </>
  );
};

export default PartnerSupportedAccessLink;
